/**
 * Arena badge, modal and countdown.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* ─── Arena ────────────────────────────────────────────────────────────────
   The pending badge lives in the HUD column (.bv-hud, z-index 1002), which is
   the one stack that renders ABOVE the full-screen panel scrims at 1001. That
   is what keeps "you can still walk around and do stuff" true with the
   inventory or the map open: the badge stays readable and its Cancel stays
   clickable. .bv-hud itself is pointer-events:none, so the badge has to grant
   itself pointer-events back or the button would be dead. */
.bv-arena-status { pointer-events: none; margin-top: 8px; }
.bv-arena-status[hidden] { display: none; }
.bv-arena-pending {
  pointer-events: auto; position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 12px;
  padding: 10px 10px 10px 18px;
  /* The worn plate the player card wears (--hud-plate, set on .bv-hud, which
     this badge sits inside), darkened so the text keeps its contrast. */
  background:
    linear-gradient(100deg, rgba(20,12,6,0.88), rgba(20,12,6,0.7)),
    var(--hud-plate, linear-gradient(165deg, rgba(42,33,22,0.92), rgba(26,20,16,0.9))) center / cover no-repeat;
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.1), inset 0 0 0 1px rgba(0,0,0,0.5),
              0 0 14px rgba(255,189,92,0.18), 0 8px 20px rgba(0,0,0,0.5);
}
/* A strip of hazard tape down the left edge: something is RUNNING. */
.bv-arena-pending::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 8px;
  background: repeating-linear-gradient(135deg, var(--brass-hot) 0 6px, #1a120a 6px 12px);
  opacity: 0.85; box-shadow: inset -1px 0 0 rgba(0,0,0,0.6);
}
/* The waiting icon: the Arena glyph in a turning dashed ring, breathing. */
.bv-arena-seek {
  position: relative; flex: none; width: 46px; height: 46px;
  display: flex; align-items: center; justify-content: center;
  color: var(--brass-hot); border-radius: 50%;
  background: radial-gradient(circle, rgba(255,189,92,0.18), rgba(0,0,0,0.55) 70%);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.6);
}
.bv-arena-seek svg {
  width: 24px; height: 24px; filter: drop-shadow(0 0 4px rgba(255,179,92,0.6));
  animation: bv-arena-breathe 1.6s ease-in-out infinite;
}
.bv-arena-seek-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 2px dashed rgba(255,189,92,0.55); border-top-color: var(--brass-hot); border-top-style: solid;
  animation: bv-arena-spin 2.4s linear infinite;
}
@keyframes bv-arena-spin { to { transform: rotate(360deg); } }
@keyframes bv-arena-breathe { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.88); opacity: 0.7; } }
.bv-arena-pending-text { display: flex; flex-direction: column; line-height: 1.25; flex: 1 1 auto; min-width: 0; }
.bv-arena-pending-label {
  font: 700 12px/1.2 var(--font-display); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--brass-bright);
}
.bv-arena-pending-sub { font: 11px/1.35 var(--font-technical); color: var(--bone-dim); white-space: nowrap; }
.bv-arena-ellipsis i { font-style: normal; animation: bv-arena-dots 1.4s infinite; opacity: 0.2; }
.bv-arena-ellipsis i:nth-child(2) { animation-delay: 0.2s; }
.bv-arena-ellipsis i:nth-child(3) { animation-delay: 0.4s; }
@keyframes bv-arena-dots { 0%, 60%, 100% { opacity: 0.2; } 30% { opacity: 1; } }
.bv-arena-pending-timer {
  font: 700 18px/1.2 var(--font-technical); color: var(--bone);
  font-variant-numeric: tabular-nums; text-shadow: 0 0 8px rgba(255,189,92,0.25);
}
/* STOP — a labelled button, not a cross (Janeks: "instead of X cross, use
   stop label"). Rust because it ends something; 44px tall for a thumb. */
.bv-arena-cancel {
  flex: none; min-width: 64px; height: 44px; padding: 0 12px; cursor: pointer;
  font: 700 12px/1 var(--font-display); letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--bone);
  background: linear-gradient(170deg, #b4512f, var(--rust) 55%, #6e2a19);
  border: 1px solid #2a0f08; border-radius: var(--radius-well);
  box-shadow: inset 0 1px 0 rgba(255,220,190,0.25), 0 3px 0 #1a0905, 0 5px 8px rgba(0,0,0,0.5);
  text-shadow: 0 1px 1px rgba(0,0,0,0.7);
  transition: transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
}
.bv-arena-cancel:hover { filter: brightness(1.15); transform: translateY(-1px); }
.bv-arena-cancel:active { transform: translateY(2px); box-shadow: inset 0 1px 0 rgba(255,220,190,0.2), 0 1px 0 #1a0905; }
@media (prefers-reduced-motion: reduce) {
  .bv-arena-seek svg, .bv-arena-seek-ring, .bv-arena-ellipsis i { animation: none; }
  .bv-arena-ellipsis i { opacity: 1; }
  .bv-arena-cancel { transition: none; }
}

/* The modal half. Unlike the badge these DO take the screen, deliberately: an
   offer with a 20-second fuse and a freeze nobody can act during are both
   moments the game should own. Above the 1001 panel scrims so an arena prompt
   is never buried under an inventory left open.

   Janeks, 2026-10-16: "Style properly the pre fight screen (accept fight), add
   more game style to it ... make sure they have some action and animation".
   The scrim is a stage: the painted pit (Higgsfield, public/ui/arena/) on a
   slow drift behind a vignette, embers rising through it, and the card as a
   riveted plate with a hazard band. Motion is transform and opacity only, and
   every keyframe below is listed in the reduced-motion block at the end. */
.bv-arena-modal { position: absolute; inset: 0; z-index: 1100; }
.bv-arena-modal[hidden] { display: none; }
.bv-arena-scrim {
  position: absolute; inset: 0; display: grid; place-items: center;
  overflow: hidden; background: #070504;
  padding: max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right))
           max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
}
.bv-arena-backdrop {
  position: absolute; inset: -5%;
  background: url('/ui/arena/versus.webp') center 40% / cover no-repeat;
  animation: bv-arena-drift 28s ease-in-out infinite alternate;
  will-change: transform;
}
/* The vignette and a floor of shadow under the card: the art has a bright sky
   and the text has to stay readable over every part of it. */
.bv-arena-backdrop::after {
  content: ''; position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 70% 60% at 50% 52%, rgba(7,5,4,0.25), rgba(7,5,4,0.82) 100%),
    linear-gradient(180deg, rgba(7,5,4,0.35), rgba(7,5,4,0) 30%, rgba(7,5,4,0.7));
}
@keyframes bv-arena-drift {
  from { transform: scale(1.02) translate3d(-1.2%, 0, 0); }
  to   { transform: scale(1.1) translate3d(1.2%, -1%, 0); }
}
/* On the confirm the art is a mood, not a scene: dimmer, so the bag grid and
   the survivor preview are what the eye lands on. */
.bv-arena-modal:has(.bv-arena-card[data-step="confirm"]) .bv-arena-backdrop { opacity: 0.45; }
@media (max-width: 720px) {
  .bv-arena-backdrop { background-image: url('/ui/arena/versus-sm.webp'); }
}

/* Embers — markup from ui/embers.ts, one span per ember, each carrying its own
   lane, size, delay and duration as custom properties. */
.bv-embers { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.bv-embers i {
  position: absolute; bottom: -12px; left: var(--x);
  width: var(--s); height: var(--s); border-radius: 50%;
  background: radial-gradient(circle, #ffe2a8 0, #ff9a3c 45%, rgba(255,110,40,0) 72%);
  opacity: 0;
  animation: bv-ember var(--t) linear var(--d) infinite;
}
@keyframes bv-ember {
  0%   { transform: translate3d(0, 0, 0); opacity: 0; }
  12%  { opacity: 0.95; }
  70%  { opacity: 0.6; }
  100% { transform: translate3d(var(--w), -92vh, 0); opacity: 0; }
}

/* ── The card: a riveted plate ─────────────────────────────────────────── */
.bv-arena-card {
  position: relative;
  width: min(560px, 100%); max-height: 86vh; max-height: 86dvh; overflow-y: auto;
  padding: 26px 24px 22px;
  background:
    linear-gradient(180deg, rgba(30,22,14,0.9), rgba(14,10,7,0.94)),
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.12), inset 0 0 0 1px rgba(0,0,0,0.55),
              0 0 0 1px rgba(0,0,0,0.6), 0 0 40px rgba(255,150,60,0.12), var(--shadow-lift);
  color: var(--bone); text-align: center;
  animation: bv-arena-card-in 0.38s cubic-bezier(.2,.9,.3,1.2) both;
}
/* Grime and scratches over the plate, under the content. */
.bv-arena-card::before {
  content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
  background:
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 420px 420px;
  opacity: 0.35; mix-blend-mode: screen;
}
.bv-arena-card > * { position: relative; }
/* Hazard band across the top edge, with a rivet at each end. */
.bv-arena-card > .bv-arena-band {
  position: absolute; left: 0; right: 0; top: 0; height: 8px;
  background: repeating-linear-gradient(135deg, var(--brass-hot) 0 9px, #1a120a 9px 18px);
  box-shadow: 0 1px 0 rgba(0,0,0,0.7);
  opacity: 0.9;
}
.bv-arena-band::before, .bv-arena-band::after {
  content: ''; position: absolute; top: 14px; width: 7px; height: 7px; border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #d9b98a, #6b4d2a 60%, #2a1c0e);
  box-shadow: 0 1px 1px rgba(0,0,0,0.7);
}
.bv-arena-band::before { left: 10px; }
.bv-arena-band::after { right: 10px; }
@keyframes bv-arena-card-in {
  from { transform: translate3d(0, 14px, 0) scale(0.94); opacity: 0; }
  to   { transform: none; opacity: 1; }
}

/* The fight steps: a wider, see-through plate so the pit shows behind it. */
.bv-arena-card:not([data-step="confirm"]) {
  width: min(660px, 100%);
  background:
    linear-gradient(180deg, rgba(20,14,9,0.5), rgba(10,7,5,0.78)),
    url('/clutter/plate.webp') 0 0 / 420px 420px;
  background-blend-mode: normal, soft-light;
}

.bv-arena-title {
  margin: 4px 0 4px; font: 700 22px/1.15 var(--font-display);
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--brass-bright);
  text-shadow: 0 2px 0 rgba(0,0,0,0.7), 0 0 18px rgba(255,170,80,0.25);
}
.bv-arena-card:not([data-step="confirm"]) .bv-arena-title {
  font-size: clamp(26px, 6vw, 36px); color: var(--brass-hot);
  text-shadow: 0 3px 0 #1a0e05, 0 0 26px rgba(255,150,60,0.55);
}
.bv-arena-title-in { animation: bv-arena-stamp 0.42s cubic-bezier(.2,.9,.3,1.3) both; }
@keyframes bv-arena-stamp {
  0%   { transform: scale(1.7); opacity: 0; letter-spacing: 0.4em; }
  60%  { transform: scale(0.96); opacity: 1; }
  100% { transform: none; letter-spacing: 0.08em; }
}
.bv-arena-sub { margin: 0 0 16px; font: 13px/1.5 var(--font-note); color: var(--bone-dim); }
.bv-arena-body { display: grid; grid-template-columns: 150px 1fr; gap: 16px; text-align: left; }
.bv-arena-body[hidden] { display: none; }
.bv-arena-preview {
  /* position:relative is LOAD-BEARING, not decoration. CharacterPreview's
     canvas is .bv-cp-canvas { position:absolute; inset:0 }, so without a
     positioned host it resolves against the nearest one that is — here the
     full-screen .bv-arena-scrim — and the survivor renders across the middle
     of the screen at viewport size. CharacterPreview now also enforces this
     itself; the declaration stays because the box should be correct in the
     stylesheet, not only after JS runs. Same pair of rules as .bv-pd-preview. */
  position: relative;
  height: 210px; border: 1px solid var(--line); border-radius: var(--radius-well);
  background: radial-gradient(circle at 50% 30%, rgba(255,170,80,0.16), rgba(0,0,0,0) 60%), var(--well);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.6), inset 0 -30px 40px rgba(0,0,0,0.45);
  overflow: hidden;
  touch-action: none; /* the canvas handles drag-to-turn via pointer events */
}
.bv-arena-loadout { min-width: 0; }
.bv-arena-loadout-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 8px; }
.bv-arena-loadout-name { font: 700 15px/1.2 var(--font-display); color: var(--bone); }
.bv-arena-loadout-lvl {
  font: 11px/1.2 var(--font-technical); color: var(--brass-bright);
  border: 1px solid var(--line); border-radius: var(--radius-tape); padding: 1px 6px;
}
.bv-arena-stats { display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; margin: 0 0 12px; }
.bv-arena-stats dt {
  font: 10px/1.6 var(--font-display); letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--brass-bright);
}
.bv-arena-stats dd { margin: 0; font: 12px/1.6 var(--font-technical); color: var(--bone); }
.bv-arena-stats dd em { color: var(--bone-faint); font-style: italic; }
.bv-arena-bag-head {
  font: 10px/1.6 var(--font-display); letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--brass-bright);
  border-top: 1px solid var(--line); padding-top: 8px;
}
.bv-arena-bag-head span { color: var(--bone); }
/* The carried bag is the shared slot grid (.bv-slots, item.css.ts) at HALF the
   pitch: Janeks asked for the inventory's grid, 2x smaller. Only the pitch,
   the gap and the height cap differ — the cells, the art and the growth rule
   are the same code the paperdoll and the trade panel use.

   39px is 78px halved. It is also what FITS: the loadout column is about
   346px wide (the card is min(560px, 92vw) with 22/24 padding, and the body is
   a 150px + 1fr grid with a 16px gap), so five columns at the full 78px pitch
   is 390px and overflows. Five at 39px is 195px.

   The height cap is its own number, not the 96px the old list used and not
   .bv-slots' 420px: four rows of ~36px plus gaps is about 165px, and the card
   already scrolls at 86vh. .bv-trade sets its own pitch the same way. */
.bv-arena-bag {
  margin-top: 6px; gap: 4px;
  max-width: calc(var(--slot-cols, 5) * 39px);
  max-height: 172px;
}

/* ── Versus: two plates either side of the emblem ──────────────────────── */
.bv-arena-vs {
  display: grid; grid-template-columns: 1fr auto 1fr; grid-template-areas: "you mid them";
  align-items: center; gap: 10px; margin: 10px 0 16px;
}
.bv-arena-vs[hidden] { display: none; }
.bv-arena-side {
  display: flex; flex-direction: column; align-items: center; gap: 3px; min-width: 0;
  padding: 12px 10px 10px;
  background: linear-gradient(180deg, rgba(22,16,10,0.92), rgba(10,7,5,0.92));
  border: 1px solid rgba(0,0,0,0.7); border-radius: var(--radius-well);
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.08), 0 8px 18px rgba(0,0,0,0.55);
}
.bv-arena-side.is-you {
  grid-area: you; border-left: 3px solid var(--moss);
  animation: bv-arena-from-left 0.5s cubic-bezier(.2,.9,.3,1.1) 0.08s both;
}
.bv-arena-side.is-them {
  grid-area: them; border-right: 3px solid var(--rust);
  animation: bv-arena-from-right 0.5s cubic-bezier(.2,.9,.3,1.1) 0.08s both;
}
@keyframes bv-arena-from-left { from { transform: translate3d(-80px, 0, 0); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes bv-arena-from-right { from { transform: translate3d(80px, 0, 0); opacity: 0; } to { transform: none; opacity: 1; } }
.bv-arena-side-tag {
  font: 700 9px/1.4 var(--font-display); letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--bone-faint);
}
.bv-arena-side.is-you .bv-arena-side-tag { color: var(--moss); }
.bv-arena-side.is-them .bv-arena-side-tag { color: #d0745a; }
.bv-arena-side-name {
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font: 700 16px/1.2 var(--font-display); color: var(--bone);
}
.bv-arena-side-lvl { font: 11px/1.2 var(--font-technical); color: var(--bone-dim); }

/* The emblem slams in after the plates land, with a flash and a jolt. */
.bv-arena-emblem {
  grid-area: mid; position: relative; width: 112px; height: 112px;
  display: grid; place-items: center;
  animation: bv-arena-slam 0.5s cubic-bezier(.3,1.4,.5,1) 0.42s both;
}
.bv-arena-emblem img {
  width: 100%; height: 100%; object-fit: contain;
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.75));
}
/* A slow breathing glow behind it once it has landed. */
.bv-arena-emblem::before {
  content: ''; position: absolute; inset: -18%; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,150,60,0.55), rgba(255,120,40,0) 65%);
  animation: bv-arena-glow 2.2s ease-in-out 1s infinite;
  opacity: 0.5;
}
.bv-arena-emblem-flash {
  position: absolute; inset: 0; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle, rgba(255,240,210,0.95), rgba(255,180,90,0.4) 40%, rgba(255,150,60,0) 70%);
  opacity: 0;
  animation: bv-arena-flash 0.6s ease-out 0.62s both;
}
@keyframes bv-arena-slam {
  0%   { transform: scale(2.6) rotate(-24deg); opacity: 0; }
  60%  { transform: scale(0.9) rotate(3deg); opacity: 1; }
  100% { transform: none; opacity: 1; }
}
@keyframes bv-arena-flash {
  0% { transform: scale(0.4); opacity: 1; }
  100% { transform: scale(2.4); opacity: 0; }
}
@keyframes bv-arena-glow {
  0%, 100% { transform: scale(0.92); opacity: 0.35; }
  50% { transform: scale(1.08); opacity: 0.75; }
}
.bv-arena-vs-word {
  grid-area: mid; align-self: end; justify-self: center; z-index: 1;
  margin-bottom: -6px; padding: 1px 10px 2px;
  font: 700 18px/1 var(--font-display); letter-spacing: 0.18em; text-transform: uppercase;
  color: #fff3dc; background: var(--rust);
  border: 1px solid #2a0f08; border-radius: var(--radius-tape);
  box-shadow: 0 3px 0 #1a0905, 0 0 14px rgba(255,90,40,0.5);
  text-shadow: 0 1px 1px rgba(0,0,0,0.6);
  animation: bv-arena-vs-pop 0.36s cubic-bezier(.3,1.5,.5,1) 0.7s both;
}
@keyframes bv-arena-vs-pop { from { transform: scale(0) rotate(-8deg); } to { transform: rotate(-4deg); } }
/* The jolt: the whole row kicks when the emblem lands. */
.bv-arena-vs:not([hidden]) { animation: bv-arena-jolt 0.32s ease-out 0.64s both; }
@keyframes bv-arena-jolt {
  0%, 100% { transform: none; }
  25% { transform: translate3d(-4px, 2px, 0); }
  50% { transform: translate3d(4px, -2px, 0); }
  75% { transform: translate3d(-2px, 1px, 0); }
}

.bv-arena-pip {
  margin-top: 4px; padding: 2px 9px;
  font: 10px/1.6 var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bone-faint); background: rgba(122,90,48,0.18);
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
.bv-arena-pip:not(.bv-arena-pip-on) { animation: bv-arena-wait 1.4s ease-in-out infinite; }
.bv-arena-pip-on {
  color: #14100b; background: var(--moss); border-color: var(--moss);
  box-shadow: 0 0 10px rgba(127,160,106,0.55); transform: scale(1.06);
}
@keyframes bv-arena-wait { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

/* The 8-second freeze. */
.bv-arena-count { margin: 6px 0 12px; }
.bv-arena-count[hidden] { display: none; }
.bv-arena-count span {
  display: inline-block;
  font: 700 clamp(76px, 22vw, 120px)/1 var(--font-display); color: var(--brass-hot);
  text-shadow: 0 5px 0 #1a0e05, 0 0 34px rgba(255,150,60,0.65); font-variant-numeric: tabular-nums;
}
.bv-arena-count-tick span { animation: bv-arena-tick 0.5s cubic-bezier(.2,.9,.3,1.3); }
@keyframes bv-arena-tick {
  0% { transform: scale(1.6); opacity: 0.3; }
  100% { transform: scale(1); opacity: 1; }
}

/* The offer's fuse: a burning bar that drains with the deadline. */
.bv-arena-fuse {
  position: relative; height: 8px; margin: 2px auto 0; width: min(360px, 100%);
  background: rgba(0,0,0,0.6); border: 1px solid rgba(0,0,0,0.8); border-radius: 4px;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.8), 0 1px 0 rgba(255,235,200,0.08);
  overflow: hidden;
}
.bv-arena-fuse[hidden] { display: none; }
.bv-arena-fuse i {
  position: absolute; inset: 0; transform-origin: left center;
  background: linear-gradient(90deg, #7a2b17, var(--rust) 40%, var(--brass-hot) 92%, #fff0c8);
  box-shadow: 0 0 10px rgba(255,150,60,0.6);
}
.bv-arena-fuse.is-low { animation: bv-arena-wait 0.5s ease-in-out infinite; }

.bv-arena-note { margin: 10px 0 0; font: 12px/1.6 var(--font-note); color: var(--bone-dim); }
.bv-arena-note span[data-deadline] { color: var(--brass-bright); font-variant-numeric: tabular-nums; }
.bv-arena-spin {
  display: inline-block; width: 12px; height: 12px; vertical-align: -1px;
  border: 2px solid var(--line); border-top-color: var(--brass-hot); border-radius: 50%;
  animation: bv-arena-spin 0.9s linear infinite;
}

/* ── Buttons ───────────────────────────────────────────────────────────── */
.bv-arena-actions { display: flex; justify-content: center; flex-wrap: wrap; gap: 12px; margin-top: 18px; }
.bv-arena-actions:empty { display: none; }
.bv-arena-btn {
  position: relative; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-width: 128px; min-height: 44px; padding: 10px 20px; cursor: pointer;
  font: 700 13px/1.2 var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  border: 1px solid #0e0905; border-radius: var(--radius-well);
  transition: transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
}
.bv-arena-btn:active { transform: translateY(2px); }
.bv-arena-btn-ghost {
  color: var(--bone-dim);
  background: linear-gradient(180deg, rgba(60,46,30,0.95), rgba(30,22,14,0.95));
  box-shadow: inset 0 1px 0 rgba(255,235,200,0.1), 0 3px 0 #0a0603, 0 5px 8px rgba(0,0,0,0.5);
}
.bv-arena-btn-ghost:hover { color: var(--bone); filter: brightness(1.15); }
.bv-arena-btn-go {
  color: #10140b;
  background: linear-gradient(170deg, #a9c98e, var(--moss) 50%, var(--moss-dark));
  box-shadow: inset 0 1px 0 rgba(240,255,220,0.45), 0 3px 0 #1d2a14, 0 5px 10px rgba(0,0,0,0.5);
  text-shadow: 0 1px 0 rgba(255,255,255,0.25);
}
.bv-arena-btn-go:hover { filter: brightness(1.1); }
/* ENTER BATTLE: bigger, breathing, with a light sweep across the face. */
.bv-arena-btn-fight { min-width: 190px; min-height: 50px; font-size: 15px; letter-spacing: 0.14em; }
.bv-arena-btn-fight svg { width: 20px; height: 20px; flex: none; }
.bv-arena-btn-fight:not(.is-done) { animation: bv-arena-call 1.3s ease-in-out infinite; }
.bv-arena-btn-fight::after {
  content: ''; position: absolute; top: -20%; bottom: -20%; width: 40%; left: -60%;
  background: linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,0.55), rgba(255,255,255,0));
  transform: skewX(-18deg);
  animation: bv-arena-sweep 2.4s ease-in-out 1.1s infinite;
}
.bv-arena-btn-fight.is-done { filter: saturate(0.6) brightness(0.9); cursor: default; }
.bv-arena-btn-fight.is-done::after { animation: none; opacity: 0; }
@keyframes bv-arena-call {
  0%, 100% { box-shadow: inset 0 1px 0 rgba(240,255,220,0.45), 0 3px 0 #1d2a14, 0 0 0 0 rgba(160,210,120,0.5); }
  50% { box-shadow: inset 0 1px 0 rgba(240,255,220,0.45), 0 3px 0 #1d2a14, 0 0 0 8px rgba(160,210,120,0); }
}
@keyframes bv-arena-sweep { 0% { left: -60%; } 55%, 100% { left: 130%; } }

/* ── Phones ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .bv-arena-card { padding: 22px 14px 16px; }
  .bv-arena-body { grid-template-columns: 1fr; }
  .bv-arena-preview { height: 170px; }
  .bv-arena-vs { gap: 6px; }
  .bv-arena-side { padding: 10px 6px 8px; }
  .bv-arena-side-name { font-size: 13px; }
  .bv-arena-emblem { width: 76px; height: 76px; }
  .bv-arena-vs-word { font-size: 14px; }
  .bv-arena-actions { gap: 8px; }
  .bv-arena-btn { min-width: 0; flex: 1 1 120px; }
  .bv-arena-btn-fight { flex-basis: 170px; }
}

@media (prefers-reduced-motion: reduce) {
  .bv-arena-backdrop, .bv-arena-card, .bv-arena-title-in, .bv-arena-side.is-you, .bv-arena-side.is-them,
  .bv-arena-emblem, .bv-arena-emblem::before, .bv-arena-emblem-flash, .bv-arena-vs-word,
  .bv-arena-vs:not([hidden]), .bv-arena-pip:not(.bv-arena-pip-on), .bv-arena-count-tick span,
  .bv-arena-fuse.is-low, .bv-arena-spin, .bv-arena-btn-fight:not(.is-done), .bv-arena-btn-fight::after {
    animation: none;
  }
  .bv-arena-emblem-flash { opacity: 0; }
  .bv-arena-vs-word { transform: rotate(-4deg); }
  .bv-embers { display: none; }
  .bv-arena-btn, .bv-arena-pip { transition: none; }
}
`;
